import React from 'react';

const G_PRIVACY = 'https://policies.google.com/privacy';
const FIREBASE_PRIVACY = 'https://firebase.google.com/support/privacy';
const APPLE_PRIVACY = 'https://www.apple.com/legal/privacy/';

export const reRoomyPrivacyExtended = {
  pageTitle: 'Privacy Policy',
  subtitle: 'ReRoomy · East IT Services s.r.o.',
  updatedBadge: 'Last updated: March 23, 2025',
  tocTitle: 'Table of Contents',
  tocItems: [
    { id: 's1', label: 'Who We Are' },
    { id: 's2', label: 'Information We Collect' },
    { id: 's3', label: 'How We Use Your Data' },
    { id: 's4', label: 'Camera & Photos' },
    { id: 's5', label: 'Third-Party Services' },
    { id: 's6', label: 'Data Storage & Retention' },
    { id: 's7', label: 'Subscriptions & Payments' },
    { id: 's8', label: 'Your Rights' },
    { id: 's9', label: "Children's Privacy" },
    { id: 's10', label: 'Changes to This Policy' },
  ],
  summary: (
    <>
      <strong>Summary:</strong> ReRoomy uses your camera/photos only to generate AI-powered room redesigns on
      your device. All generated images are stored locally on your device. Only anonymous usage analytics are
      sent to our servers. We never sell your personal data.
    </>
  ),
  sections: [
    {
      id: 's1',
      num: '01',
      title: 'Who We Are',
      body: (
        <>
          <p>
            ReRoomy is a mobile application developed and operated by <strong>East IT Services s.r.o.</strong>{' '}
            (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). ReRoomy allows users to take or upload photos
            of their rooms and generate redesigned versions using artificial intelligence powered by Google&apos;s
            Gemini models.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, and protect your information when you use our
            application. By downloading or using ReRoomy, you agree to the practices described in this policy.
          </p>
          <p>
            For questions about this policy, you may contact us at{' '}
            <a href="mailto:reroomyapp@outlook.com">reroomyapp@outlook.com</a>.
          </p>
        </>
      ),
    },
    {
      id: 's2',
      num: '02',
      title: 'Information We Collect',
      body: (
        <>
          <p>We collect only the minimum information necessary to provide our services.</p>
          <table className="app-legal-doc__data-table">
            <thead>
              <tr>
                <th>Data Type</th>
                <th>What We Collect</th>
                <th>Where It&apos;s Stored</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Photos / Camera</td>
                <td>Images you take or select to generate room redesigns</td>
                <td>On your device only</td>
              </tr>
              <tr>
                <td>Generated Images</td>
                <td>AI-generated room design outputs</td>
                <td>On your device only</td>
              </tr>
              <tr>
                <td>Usage Analytics</td>
                <td>Anonymous feature usage events, crash reports, session data</td>
                <td>Our analytics servers (anonymised)</td>
              </tr>
              <tr>
                <td>Subscription Status</td>
                <td>Managed entirely by Apple — we do not access payment details</td>
                <td>Apple servers</td>
              </tr>
            </tbody>
          </table>
          <p>
            We do <strong>not</strong> collect your name, email address, location, contacts, browsing history, or
            any other personal identifiers.
          </p>
        </>
      ),
    },
    {
      id: 's3',
      num: '03',
      title: 'How We Use Your Data',
      body: (
        <>
          <p>The information we collect is used solely for the following purposes:</p>
          <ul>
            <li>To process your photos through AI models and generate room redesign images</li>
            <li>To improve the performance, stability, and features of the application</li>
            <li>To understand which features are used most so we can prioritise improvements</li>
            <li>To detect and fix technical errors and crashes</li>
            <li>To verify and manage your subscription status via Apple</li>
          </ul>
          <p>We do not use your data for advertising, profiling, or any purpose beyond operating and improving ReRoomy.</p>
        </>
      ),
    },
    {
      id: 's4',
      num: '04',
      title: 'Camera & Photo Access',
      body: (
        <>
          <p>
            ReRoomy requires access to your device&apos;s camera and photo library to function. This access is
            used exclusively to:
          </p>
          <ul>
            <li>Capture a photo of a room using your camera</li>
            <li>Select an existing photo from your photo library</li>
            <li>Save the AI-generated redesign image to your photo library (with your permission)</li>
          </ul>
          <div className="app-legal-doc__highlight">
            <strong>Important:</strong> Your photos are processed using the Google Gemini API to generate room
            redesigns. When a photo is submitted for generation, it is transmitted to Google&apos;s servers for
            processing. Please review{' '}
            <a href={G_PRIVACY} target="_blank" rel="noopener noreferrer">
              Google&apos;s Privacy Policy
            </a>{' '}
            to understand how they handle image data. Generated results are returned to your device and stored
            locally — we do not retain copies on our own servers.
          </div>
          <p>
            You may revoke camera and photo library access at any time in your device&apos;s Settings. Revoking
            access will prevent you from using the core features of ReRoomy.
          </p>
        </>
      ),
    },
    {
      id: 's5',
      num: '05',
      title: 'Third-Party Services',
      body: (
        <>
          <p>
            ReRoomy integrates with the following third-party services. Each has its own privacy practices:
          </p>
          <h3>Google Gemini API</h3>
          <p>
            Used to process photos and generate AI room redesigns. When you generate an image, your photo is sent
            to Google&apos;s servers. We recommend reviewing{' '}
            <a href={G_PRIVACY} target="_blank" rel="noopener noreferrer">
              Google&apos;s Privacy Policy
            </a>
            .
          </p>
          <h3>Firebase Analytics (Google)</h3>
          <p>
            We use Firebase Analytics to collect anonymous, aggregated usage data such as which features are used
            and how often. This data is not linked to your identity. See{' '}
            <a href={FIREBASE_PRIVACY} target="_blank" rel="noopener noreferrer">
              Firebase Privacy Information
            </a>
            .
          </p>
          <h3>Apple In-App Purchases</h3>
          <p>
            Subscription management, billing, and payments are handled entirely by Apple. We do not receive or
            store your payment information. See{' '}
            <a href={APPLE_PRIVACY} target="_blank" rel="noopener noreferrer">
              Apple&apos;s Privacy Policy
            </a>
            .
          </p>
        </>
      ),
    },
    {
      id: 's6',
      num: '06',
      title: 'Data Storage & Retention',
      body: (
        <>
          <p>
            <strong>On-device data:</strong> All photos and generated images are stored exclusively on your
            device. We have no access to this data. You may delete it at any time by removing images from your
            photo library or uninstalling the app.
          </p>
          <p>
            <strong>Analytics data:</strong> Anonymous usage analytics are retained for up to 24 months to
            identify long-term trends and improve the app. This data cannot be used to identify you individually.
          </p>
          <p>
            <strong>Data deletion:</strong> Because we do not collect personal data linked to your identity, there
            is no account to delete. Uninstalling ReRoomy removes all locally stored app data from your device.
          </p>
        </>
      ),
    },
    {
      id: 's7',
      num: '07',
      title: 'Subscriptions & Payments',
      body: (
        <>
          <p>
            ReRoomy offers auto-renewable subscriptions to unlock premium features. All subscription management
            is handled through Apple&apos;s App Store infrastructure:
          </p>
          <ul>
            <li>Subscriptions are billed through your Apple ID</li>
            <li>You can manage or cancel subscriptions in your device&apos;s Settings → Apple ID → Subscriptions</li>
            <li>We do not have access to your credit card, billing address, or any payment details</li>
            <li>Refund requests must be directed to Apple</li>
          </ul>
        </>
      ),
    },
    {
      id: 's8',
      num: '08',
      title: 'Your Rights',
      body: (
        <>
          <p>Depending on your location, you may have the following rights regarding your data:</p>
          <ul>
            <li>
              <strong>Access:</strong> Request information about what data we hold about you
            </li>
            <li>
              <strong>Deletion:</strong> Request deletion of any data associated with you
            </li>
            <li>
              <strong>Opt-out of analytics:</strong> You may disable analytics tracking in your device&apos;s
              Privacy settings (iOS: Settings → Privacy &amp; Security → Analytics &amp; Improvements)
            </li>
            <li>
              <strong>GDPR (EU/EEA residents):</strong> You have the right to access, rectify, erase, restrict, or
              object to processing of your personal data
            </li>
            <li>
              <strong>CCPA (California residents):</strong> You have the right to know, delete, and opt out of
              the sale of personal information. We do not sell personal data.
            </li>
          </ul>
          <p>
            To exercise any of these rights, contact us at{' '}
            <a href="mailto:reroomyapp@outlook.com">reroomyapp@outlook.com</a>. We will respond within 30
            days.
          </p>
        </>
      ),
    },
    {
      id: 's9',
      num: '09',
      title: "Children's Privacy",
      body: (
        <>
          <p>
            ReRoomy is not directed at children under the age of 13. We do not knowingly collect personal
            information from children under 13. If you are a parent or guardian and believe your child has
            provided us with personal information, please contact us at{' '}
            <a href="mailto:reroomyapp@outlook.com">reroomyapp@outlook.com</a> and we will promptly take
            steps to address the situation.
          </p>
          <p>
            For users in the European Union, this age threshold is 16 years, or the applicable age of digital
            consent in your country.
          </p>
        </>
      ),
    },
    {
      id: 's10',
      num: '10',
      title: 'Changes to This Policy',
      body: (
        <>
          <p>
            We may update this Privacy Policy from time to time. When we do, we will revise the &quot;Last
            updated&quot; date at the top of this page. For significant changes, we will notify you through the
            app or via the App Store update notes. Your continued use of ReRoomy after any changes constitutes
            your acceptance of the updated policy.
          </p>
        </>
      ),
    },
  ],
};
